import React, { useState } from 'react';
import { Box, Typography, alpha } from '@mui/material';
import { LOJ_COLORS } from '../theme/lojTheme';
import { SandseaConquestData, SandseaPhase, SandseaSquadNote } from '../data/strategies';
import OrnamentalDivider from './OrnamentalDivider';
import SandseaRoster from './SandseaRoster';
import { useTranslation } from '../i18n/LanguageContext';

// ─── Constants ────────────────────────────────────────────────────────────────

const ACCENT = LOJ_COLORS.sandLight;
const GLOW   = LOJ_COLORS.sandGlow;

// Squad color pills — cycle through a set of toned colors
const SQUAD_COLORS = [
  { bg: alpha(LOJ_COLORS.crystalDeep, 0.8),  text: LOJ_COLORS.crystalLight  },
  { bg: alpha(LOJ_COLORS.goldDim,     0.5),  text: LOJ_COLORS.goldBright    },
  { bg: alpha(LOJ_COLORS.bloodDark,   0.7),  text: LOJ_COLORS.ember         },
  { bg: alpha(LOJ_COLORS.sandDeep,    0.8),  text: LOJ_COLORS.sandLight     },
  { bg: alpha(LOJ_COLORS.crystalMid,  0.6),  text: LOJ_COLORS.crystalGlow   },
  { bg: alpha(LOJ_COLORS.midStone,    0.9),  text: LOJ_COLORS.parchmentLight},
];

// ─── Squad note row ───────────────────────────────────────────────────────────

const SquadNoteRow: React.FC<{ note: SandseaSquadNote; index: number }> = ({ note, index }) => {
  const { t } = useTranslation();
  const c = SQUAD_COLORS[index % SQUAD_COLORS.length];
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 2,
        p: 1.5,
        background: alpha(LOJ_COLORS.void, 0.35),
        border: `1px solid ${alpha(c.text, 0.2)}`,
        borderLeft: `3px solid ${c.text}`,
        mb: 1,
      }}
    >
      <Box
        sx={{
          flexShrink: 0,
          px: 1.5,
          py: 0.2,
          background: c.bg,
          border: `1px solid ${alpha(c.text, 0.5)}`,
          fontFamily: "'Cinzel', serif",
          fontSize: '0.68rem',
          letterSpacing: '0.1em',
          color: c.text,
          fontWeight: 700,
          whiteSpace: 'nowrap',
          mt: '2px',
        }}
      >
        {note.squad.toUpperCase()}
      </Box>
      <Typography
        variant="body2"
        sx={{ color: LOJ_COLORS.parchmentLight, fontSize: '1rem', lineHeight: 1.65, fontFamily: "'Crimson Text', Georgia, serif" }}
      >
        {t(note.instruction)}
      </Typography>
    </Box>
  );
};

// ─── Reusable framed map image ────────────────────────────────────────────────

interface FramedImageProps {
  src: string;
  alt: string;
  label: string;
  accentColor: string;
  glowColor: string;
  maxWidth?: number;
}

const FramedImage: React.FC<FramedImageProps> = ({ src, alt, label, accentColor, glowColor, maxWidth = 680 }) => (
  <Box
    sx={{
      position: 'relative',
      border: `1px solid ${alpha(accentColor, 0.35)}`,
      boxShadow: `0 0 0 1px ${alpha(LOJ_COLORS.void, 0.8)}, 0 8px 32px ${alpha(LOJ_COLORS.void, 0.9)}, 0 0 40px ${alpha(glowColor, 0.1)}`,
      '&::before': {
        content: '""',
        position: 'absolute',
        inset: 0,
        background: `linear-gradient(180deg, transparent 70%, ${alpha(LOJ_COLORS.abyss, 0.4)} 100%)`,
        pointerEvents: 'none',
        zIndex: 1,
      },
    }}
  >
    {/* Corner ornaments */}
    {[
      { top: 0,    left: 0,    borderTop: true,    borderLeft: true    },
      { top: 0,    right: 0,   borderTop: true,    borderRight: true   },
      { bottom: 0, left: 0,    borderBottom: true, borderLeft: true    },
      { bottom: 0, right: 0,   borderBottom: true, borderRight: true   },
    ].map((pos, i) => (
      <Box
        key={i}
        sx={{
          position: 'absolute',
          zIndex: 2,
          width: 20,
          height: 20,
          ...pos,
          borderTop:    (pos as any).borderTop    ? `2px solid ${alpha(accentColor, 0.7)}` : 'none',
          borderBottom: (pos as any).borderBottom ? `2px solid ${alpha(accentColor, 0.7)}` : 'none',
          borderLeft:   (pos as any).borderLeft   ? `2px solid ${alpha(accentColor, 0.7)}` : 'none',
          borderRight:  (pos as any).borderRight  ? `2px solid ${alpha(accentColor, 0.7)}` : 'none',
          pointerEvents: 'none',
        }}
      />
    ))}

    <Box
      component="img"
      src={src}
      alt={alt}
      sx={{
        display: 'block',
        width: '100%',
        maxWidth,
        mx: 'auto',
        height: 'auto',
        filter: 'brightness(0.93) contrast(1.04) saturate(1.08)',
      }}
    />

    {/* Label */}
    <Box
      sx={{
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 2,
        display: 'flex',
        justifyContent: 'center',
        pb: 1,
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 0.4,
          background: alpha(LOJ_COLORS.void, 0.85),
          border: `1px solid ${alpha(accentColor, 0.4)}`,
          fontFamily: "'Cinzel', serif",
          fontSize: '0.65rem',
          letterSpacing: '0.2em',
          color: accentColor,
        }}
      >
        ✦ {label.toUpperCase()} ✦
      </Box>
    </Box>
  </Box>
);

const PhaseCard: React.FC<{ phase: SandseaPhase; index: number; isActive: boolean; onClick: () => void }> = ({
  phase, index, isActive, onClick,
}) => {
  const { t } = useTranslation();
  return (
  <Box
    onClick={onClick}
    sx={{
      flex: '1 1 140px',
      cursor: 'pointer',
      p: 2,
      background: isActive
        ? `linear-gradient(135deg, ${alpha(ACCENT, 0.18)}, ${alpha(ACCENT, 0.08)})`
        : `linear-gradient(135deg, ${alpha(LOJ_COLORS.darkStone, 0.9)}, ${alpha(LOJ_COLORS.stone, 0.7)})`,
      border: `1px solid ${isActive ? alpha(ACCENT, 0.7) : alpha(ACCENT, 0.2)}`,
      borderTop: isActive ? `3px solid ${ACCENT}` : `1px solid ${alpha(ACCENT, 0.2)}`,
      boxShadow: isActive ? `0 0 24px ${alpha(GLOW, 0.2)}, inset 0 0 20px ${alpha(ACCENT, 0.05)}` : 'none',
      transition: 'all 0.25s ease',
      textAlign: 'center',
      '&:hover': {
        borderColor: alpha(ACCENT, isActive ? 0.7 : 0.45),
        background: `linear-gradient(135deg, ${alpha(ACCENT, isActive ? 0.18 : 0.1)}, ${alpha(ACCENT, 0.05)})`,
      },
    }}
  >
    <Typography sx={{ fontSize: '1.4rem', mb: 0.5 }}>{phase.icon}</Typography>
    <Typography
      sx={{
        fontFamily: "'Cinzel', serif",
        fontWeight: 700,
        fontSize: '0.85rem',
        letterSpacing: '0.08em',
        color: isActive ? ACCENT : LOJ_COLORS.parchment,
        textShadow: isActive ? `0 0 12px ${alpha(GLOW, 0.5)}` : 'none',
      }}
    >
      {phase.name}
    </Typography>
    <Typography
      sx={{
        fontFamily: "'Cinzel', serif",
        fontSize: '0.7rem',
        letterSpacing: '0.05em',
        color: isActive ? alpha(ACCENT, 0.8) : LOJ_COLORS.goldDim,
        mt: 0.3,
      }}
    >
      {t(phase.duration)}
    </Typography>
  </Box>
  );
};

// ─── Phase detail panel ───────────────────────────────────────────────────────

const PhaseDetail: React.FC<{ phase: SandseaPhase }> = ({ phase }) => {
  const { t } = useTranslation();
  return (
  <Box
    sx={{
      animation: 'phaseFade 0.3s ease',
      '@keyframes phaseFade': {
        from: { opacity: 0, transform: 'translateY(6px)' },
        to:   { opacity: 1, transform: 'translateY(0)'   },
      },
    }}
  >
    {/* Overview quote */}
    <Box
      sx={{
        p: { xs: 2, md: 3 },
        mb: 3,
        background: `linear-gradient(135deg, ${alpha(LOJ_COLORS.darkStone, 0.8)}, ${alpha(LOJ_COLORS.stone, 0.6)})`,
        border: `1px solid ${alpha(ACCENT, 0.2)}`,
        position: 'relative',
      }}
    >
      <Typography
        sx={{ position: 'absolute', top: -12, left: 20, fontSize: '3rem', lineHeight: 1, color: alpha(ACCENT, 0.25), fontFamily: 'Georgia, serif' }}
      >
        "
      </Typography>
      <Typography variant="body1" sx={{ fontStyle: 'italic', color: LOJ_COLORS.parchment, fontSize: '1.1rem', lineHeight: 1.8 }}>
        {t(phase.overview)}
      </Typography>
    </Box>

    {/* Instructions */}
    <Box
      sx={{
        mb: phase.squadNotes ? 3 : 0,
        p: { xs: 2.5, md: 3.5 },
        background: `linear-gradient(135deg, ${alpha(LOJ_COLORS.darkStone, 0.95)}, ${alpha(LOJ_COLORS.stone, 0.85)})`,
        border: `1px solid ${alpha(ACCENT, 0.3)}`,
        borderLeft: `3px solid ${ACCENT}`,
        boxShadow: `0 4px 24px ${alpha(LOJ_COLORS.void, 0.8)}, inset 0 1px 0 ${alpha(ACCENT, 0.15)}`,
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0, left: 0, right: 0,
          height: '1px',
          background: `linear-gradient(to right, ${ACCENT}, transparent)`,
          opacity: 0.5,
        },
        position: 'relative',
      }}
    >
      <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
        {phase.instructions.map((line, i) => (
          <Box
            component="li"
            key={i}
            sx={{
              mb: 1.2,
              color: LOJ_COLORS.parchmentLight,
              fontSize: '1.05rem',
              lineHeight: 1.7,
              fontFamily: "'Crimson Text', Georgia, serif",
              '&::marker': { color: alpha(ACCENT, 0.7) },
            }}
          >
            {t(line)}
          </Box>
        ))}
      </Box>
    </Box>

    {/* Map image */}
    {phase.mapImage && (
      <Box sx={{ mt: 3, mb: phase.squadNotes ? 0 : 3 }}>
        <FramedImage
          src={phase.mapImage}
          alt={`${phase.name} map`}
          label={`${phase.name} — ${t('Zone Map')}`}
          accentColor={ACCENT}
          glowColor={GLOW}
        />
      </Box>
    )}

    {/* Squad notes */}
    {phase.squadNotes && phase.squadNotes.length > 0 && (
      <Box sx={{ mt: 3 }}>
        <OrnamentalDivider label={t('Squad Orders')} color={LOJ_COLORS.goldBright} />
        {phase.squadNotes.map((note, i) => (
          <SquadNoteRow key={i} note={note} index={i} />
        ))}
      </Box>
    )}

    {/* Final push callout */}
    {phase.finalPush && (
      <Box
        sx={{
          mt: 3,
          p: 2,
          background: `linear-gradient(135deg, ${alpha(LOJ_COLORS.goldDim, 0.2)}, ${alpha(LOJ_COLORS.darkStone, 0.7)})`,
          border: `1px solid ${alpha(LOJ_COLORS.goldBright, 0.45)}`,
          borderLeft: `3px solid ${LOJ_COLORS.goldBright}`,
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1.5,
        }}
      >
        <Typography sx={{ color: LOJ_COLORS.goldBright, fontSize: '1rem', flexShrink: 0, mt: '2px' }}>🏆</Typography>
        <Typography
          variant="body2"
          sx={{ color: LOJ_COLORS.goldShine, fontSize: '1.02rem', fontFamily: "'Crimson Text', Georgia, serif", fontWeight: 600, lineHeight: 1.65 }}
        >
          {t(phase.finalPush)}
        </Typography>
      </Box>
    )}

    {/* Warning */}
    {phase.warning && (
      <Box
        sx={{
          mt: 3,
          p: 2,
          background: `linear-gradient(135deg, ${alpha(LOJ_COLORS.bloodDark, 0.7)}, ${alpha(LOJ_COLORS.stone, 0.5)})`,
          border: `1px solid ${alpha(LOJ_COLORS.ember, 0.4)}`,
          borderLeft: `3px solid ${LOJ_COLORS.ember}`,
          display: 'flex',
          alignItems: 'flex-start',
          gap: 1.5,
        }}
      >
        <Typography sx={{ fontSize: '1rem', flexShrink: 0, color: LOJ_COLORS.ember }}>⚠</Typography>
        <Typography
          variant="body2"
          sx={{ color: alpha(LOJ_COLORS.ember, 0.9), fontSize: '0.98rem', fontFamily: "'Crimson Text', Georgia, serif", fontWeight: 600 }}
        >
          {t(phase.warning)}
        </Typography>
      </Box>
    )}
  </Box>
  );
};

// ─── Main panel ───────────────────────────────────────────────────────────────

interface SandseaConquestPanelProps {
  strategy: SandseaConquestData;
  hidden: boolean;
}

const SandseaConquestPanel: React.FC<SandseaConquestPanelProps> = ({ strategy, hidden }) => {
  const { t } = useTranslation();
  const [activePhase, setActivePhase] = useState(0);

  return (
    <Box
      role="tabpanel"
      hidden={hidden}
      sx={{
        display: hidden ? 'none' : 'block',
        animation: hidden ? 'none' : 'fadeIn 0.4s ease',
        '@keyframes fadeIn': {
          from: { opacity: 0, transform: 'translateY(10px)' },
          to:   { opacity: 1, transform: 'translateY(0)'    },
        },
      }}
    >
      {/* Event header */}
      <Box
        sx={{
          position: 'relative',
          mb: 4,
          pt: 5,
          pb: 4,
          px: { xs: 3, md: 5 },
          background: `linear-gradient(180deg, ${alpha(LOJ_COLORS.void, 0.4)} 0%, ${alpha(ACCENT, 0.04)} 50%, transparent 100%)`,
          borderBottom: `1px solid ${alpha(ACCENT, 0.2)}`,
          textAlign: 'center',
        }}
      >
        {/* Corner ornaments */}
        {['topleft', 'topright'].map((pos) => (
          <Box
            key={pos}
            sx={{
              position: 'absolute',
              top: 12,
              ...(pos === 'topleft' ? { left: 12 } : { right: 12 }),
              width: 40,
              height: 40,
              borderTop: `2px solid ${alpha(ACCENT, 0.5)}`,
              ...(pos === 'topleft'
                ? { borderLeft: `2px solid ${alpha(ACCENT, 0.5)}` }
                : { borderRight: `2px solid ${alpha(ACCENT, 0.5)}` }),
            }}
          />
        ))}

        <Typography
          variant="h2"
          sx={{
            fontSize: { xs: '2rem', md: '2.8rem', lg: '3.2rem' },
            color: ACCENT,
            textShadow: `0 0 20px ${alpha(GLOW, 0.6)}, 0 0 40px ${alpha(GLOW, 0.3)}, 0 2px 4px ${alpha(LOJ_COLORS.void, 0.9)}`,
            mb: 1,
          }}
        >
          {strategy.eventName}
        </Typography>

        <Typography variant="subtitle1" sx={{ color: LOJ_COLORS.parchment, letterSpacing: '0.2em', fontSize: '0.8rem', opacity: 0.8 }}>
          — {t(strategy.subtitle)} —
        </Typography>
      </Box>

      <Box sx={{ px: { xs: 2, md: 4 } }}>

        {/* Phase selector */}
        <OrnamentalDivider label={t('Select Phase')} color={ACCENT} />
        <Box sx={{ display: 'flex', gap: 2, mb: 4, flexWrap: 'wrap' }}>
          {strategy.phases.map((phase, i) => (
            <PhaseCard
              key={phase.name}
              phase={phase}
              index={i}
              isActive={activePhase === i}
              onClick={() => setActivePhase(i)}
            />
          ))}
        </Box>

        {/* Active phase detail */}
        <OrnamentalDivider label={`${strategy.phases[activePhase].name} — ${t(strategy.phases[activePhase].duration)}`} color={ACCENT} />
        <PhaseDetail phase={strategy.phases[activePhase]} />

        {/* Tactician's note */}
        <OrnamentalDivider label={t("Tactician's Ability")} color={LOJ_COLORS.crystalLight} />
        <Box
          sx={{
            p: { xs: 2, md: 3 },
            mb: 4,
            background: `linear-gradient(135deg, ${alpha(LOJ_COLORS.crystalDeep, 0.6)}, ${alpha(LOJ_COLORS.darkStone, 0.8)})`,
            border: `1px solid ${alpha(LOJ_COLORS.crystalLight, 0.35)}`,
            borderLeft: `3px solid ${LOJ_COLORS.crystalLight}`,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.5 }}>
            <Typography sx={{ fontSize: '1.1rem', flexShrink: 0, mt: '1px' }}>🔭</Typography>
            <Typography
              variant="body1"
              sx={{ color: LOJ_COLORS.parchmentLight, fontSize: '1.05rem', lineHeight: 1.75, fontFamily: "'Crimson Text', Georgia, serif" }}
            >
              {t(strategy.tacticiansNote)}
            </Typography>
          </Box>
        </Box>

        {/* Pinned guide */}
        {strategy.pinnedGuide && (
          <>
            <OrnamentalDivider label={t('Pinned Guide')} color={LOJ_COLORS.parchmentDark} />
            <Box sx={{ mb: 4 }}>
              <FramedImage
                src={strategy.pinnedGuide}
                alt="Sandsea Conquest complete guide"
                label={`${strategy.eventName} — ${t('Complete Guide & Tips')}`}
                accentColor={LOJ_COLORS.parchmentDark}
                glowColor={LOJ_COLORS.parchment}
                maxWidth={600}
              />
            </Box>
          </>
        )}

        {/* Commander's Notes */}
        <OrnamentalDivider label={t("Commander's Notes")} color={LOJ_COLORS.goldBright} />
        <Box
          sx={{
            p: { xs: 2.5, md: 4 },
            background: `linear-gradient(135deg, ${alpha(LOJ_COLORS.void, 0.8)}, ${alpha(LOJ_COLORS.darkStone, 0.9)})`,
            border: `1px solid ${alpha(LOJ_COLORS.goldBright, 0.25)}`,
            position: 'relative',
            mb: 4,
            '&::before': {
              content: '""',
              position: 'absolute',
              inset: 4,
              border: `1px solid ${alpha(LOJ_COLORS.goldDim, 0.2)}`,
              pointerEvents: 'none',
            },
          }}
        >
          <Typography
            variant="h4"
            sx={{ mb: 3, fontSize: '1.1rem', color: LOJ_COLORS.goldBright, display: 'flex', alignItems: 'center', gap: 1 }}
          >
            <span>⚜</span> {t("Commander's Notes")}
          </Typography>
          <Box component="ol" sx={{ m: 0, pl: 3, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {strategy.commandersNotes.map((note, i) => (
              <Box
                component="li"
                key={i}
                sx={{
                  color: LOJ_COLORS.parchmentLight,
                  fontSize: '1.05rem',
                  lineHeight: 1.7,
                  fontFamily: "'Crimson Text', Georgia, serif",
                  pl: 0.5,
                  '&::marker': { color: LOJ_COLORS.goldBright, fontFamily: "'Cinzel', serif", fontWeight: 600 },
                }}
              >
                {t(note)}
              </Box>
            ))}
          </Box>
        </Box>

        {/* Roster */}
        <SandseaRoster />

      </Box>
    </Box>
  );
};

export default SandseaConquestPanel;
